import { cn } from "@/lib/utils";
import { forwardRef, useCallback, useImperativeHandle, useRef, useState } from "react";
import { Textarea } from "@/components/TextArea";
import { Loader2, Mic, MicOff, Pencil } from "lucide-react";
import { useSpeechToTextRecorder } from "@/contexts/speechToText/useSpeechToTextRecorder";
import { MarkdownH3, TaskMarkdown } from "../TaskMarkdown";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const MIC_PERMISSION_DENIED_MESSAGE = "Mikrofonåtkomst är blockerad. Tillåt mikrofonen i webbläsarens inställningar för att använda taligenkänning.";
const SESSION_LIMIT_MESSAGE = "Inspelningen stoppades efter 2 minuter. Tryck på mikrofonen igen för att fortsätta.";

function appendWithSeparator(base: string, addition: string) {
    if (!addition) return base;
    const separator = base && !/\s$/.test(base) ? " " : "";
    return base + separator + addition;
}

export type TaskTextareaProps = {
    id: string;
    label?: string;
    lines?: number;
    defaultValue: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
};

type TaskTextareaRef = {
    textarea: HTMLTextAreaElement;
    resetValue: () => void;
};
const TaskTextarea = forwardRef<TaskTextareaRef, TaskTextareaProps>(function TaskTextarea({ id, label, lines, defaultValue, onChange, disabled }, ref) {
    if (!id) {
        console.warn("Textarea is missing an id");
    }

    const [value, setValue] = useState<string>(defaultValue);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const valueRef = useRef(value);
    valueRef.current = value;

    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    const appendTranscript = useCallback((text: string) => {
        const newValue = appendWithSeparator(valueRef.current, text);
        valueRef.current = newValue;
        setValue(newValue);
        onChangeRef.current?.(newValue);
    }, []);

    const { isRecording, isConnecting, micPermissionDenied, partialText, sessionLimitReached, toggle } = useSpeechToTextRecorder(appendTranscript);

    // Live preview only — never written back into value/onChange, so it can't affect the
    // persisted content unless/until it's actually committed (appendTranscript above).
    const displayValue = appendWithSeparator(value, partialText);

    useImperativeHandle(ref, () => ({
        resetValue: () => setValue(defaultValue),
        get textarea() { return textareaRef.current; },
    }));

    return <div className="flex flex-col gap-4 my-4">
        <span className="flex text-base items-center">
            <Pencil className="p-1 text-step-current" />
            <TaskMarkdown
                components={{
                    h3({ children }) { return <MarkdownH3 className="p-0" children={children} />; }
                }}
            >
                {`### *${label}*`}
            </TaskMarkdown>
            <div className="grow flex flex-row-reverse relative">
                <Popover>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="step-current-outline"
                            className={cn(
                                "h-6 w-6 ml-1 p-1",
                                // isRecording/micPermissionDenied fully override the variant's hover with
                                // their own state color; scoped to [@media(hover:hover)] for the same reason.
                                isRecording && "border-destructive/30 bg-destructive text-accent-foreground animate-pulse [@media(hover:hover)]:hover:bg-background [@media(hover:hover)]:hover:border-destructive [@media(hover:hover)]:hover:text-destructive",
                                micPermissionDenied && "opacity-50 border-muted-foreground text-muted-foreground"
                            )}
                            onClick={toggle}
                            disabled={disabled}
                            aria-pressed={isRecording}
                            aria-disabled={micPermissionDenied}
                            aria-label={micPermissionDenied ? MIC_PERMISSION_DENIED_MESSAGE : isRecording ? "Stoppa inspelning" : "Starta inspelning"}
                            title={micPermissionDenied ? MIC_PERMISSION_DENIED_MESSAGE : undefined}
                        >
                            {isConnecting ? <Loader2 className="animate-spin" /> : micPermissionDenied ? <MicOff /> : <Mic />}
                        </Button>
                    </PopoverTrigger>
                    {micPermissionDenied && (
                        <PopoverContent side="top" collisionPadding={24} className="p-3 w-auto max-w-64 text-sm">
                            {MIC_PERMISSION_DENIED_MESSAGE}
                        </PopoverContent>
                    )}
                </Popover>
                {sessionLimitReached && (
                    <div
                        role="status"
                        className="absolute bottom-full right-0 mb-1 z-50 w-auto max-w-64 whitespace-normal rounded-md border bg-popover p-3 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95"
                    >
                        {SESSION_LIMIT_MESSAGE}
                    </div>
                )}
            </div>
        </span>
        <Textarea
            id={id}
            rows={lines}
            ref={textareaRef}
            value={displayValue}
            className={cn(
                "min-h-[120px] text-base ring-1 ring-offset-2 ring-step-current focus-visible:ring-step-current",
                lines && "min-h-0"
            )}
            onChange={(e) => {
                valueRef.current = e.currentTarget.value;
                setValue(e.currentTarget.value);
                onChange?.(e.currentTarget.value);
            }}
            // Typing while this field is the one being transcribed into would conflict with
            // the live partial preview and incoming committed text — block editing for the
            // duration instead of trying to reconcile the two.
            readOnly={isRecording || isConnecting}
            disabled={disabled}
        />
    </div >;
});

export { TaskTextarea };
